<template>
  <div class="mb-0">
    <Menubar :model="menubarItems">
      <template #item="{ item, props }">
        <div
          v-if="item.separator"
          class="flex items-center px-2"
        >
          <span class="h-6 w-px bg-gray-300" />
        </div>
        <a
          v-else
          v-ripple
          class="flex items-center"
          :class="{ 'active-route text-white': isActiveRoute(item.route) }"
          v-bind="props.action"
          @click="onMenuClick(item.route)"
        >
          <span :class="isActiveRoute(item.route) ? 'ml-2 text-white' : 'ml-2'">
            {{ item.label }}
          </span>
          <span
            :class="[item.icon, isActiveRoute(item.route) ? 'text-white' : '']"
          />
        </a>
      </template>

      <template #end>
        <div class="flex items-center gap-2">
          <Avatar
            class="cursor-pointer"
            :label="Helper.getInitials() || null"
            :icon="Helper.getInitials() ? null : 'pi pi-user'"
            shape="circle"
            @click="profileToggle"
            size="large"
            :style="{ color: userStore.isOnline ? 'green' : 'red' }"
          />
          <TieredMenu
            ref="profileMenuRef"
            id="overlay_tmenu"
            :model="profileMenu"
            popup
          >
            <template #item="{ item, props }">
              <a
                v-if="item.clickable || item.command"
                v-ripple
                v-bind="props.action"
                :class="{ 'active-route': isActiveRoute(item.route) }"
              >
                <span
                  :class="item.icon"
                  :style="{ color: item.iconColor }"
                />
                <span class="ml-2">{{ item.label }}</span>
              </a>
              <!-- Non clickable items. Closing menu is prevented with @click.stop-->
              <div
                v-else
                class="flex items-center px-3 py-2 text-color-secondary"
                @click.stop
              >
                <span
                  :class="item.icon"
                  :style="{ color: item.iconColor }"
                />
                <span class="ml-2">{{ item.label }}</span>
              </div>
            </template>
          </TieredMenu>
        </div>
      </template>
    </Menubar>
    <!-- Environment Tag -->
    <Tag
      v-if="!Helper.isCurrentEnvironmentProd()"
      class="w-full"
      :severity="Helper.isCurrentEnvironmentDev() ? `danger` : `warn`"
      :value="`${Helper.getCurrentEnvironment()}`"
    />
    <Tag
      v-if="Helper.isSessionExpiringSoon().isExpired"
      class="w-full"
      icon="pi pi-exclamation-triangle"
      severity="primary"
      :value="`Warning ! - Your session got expired on at ${
        Helper.isSessionExpiringSoon().local
      }, please connect to the internet to get it refreshed.`"
    />
  </div>
  <ConfirmationDialog
    :visible="logoutDialog"
    :headerMsg="`Logout Confirm`"
    :message="'Are you sure you want to logout?'"
    :onConfirm="logOutConfirm"
    @update:visible="logoutDialog = $event"
  />
  <ErrorDialog
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    :onConfirm="onErrorDialogClose"
    @update:visible="errorObj.isVisible = $event"
  />
</template>

<style>
  .active-route {
    color: white; /* Customize the color for the active route */
    background-color: #f98023;
    border-radius: 5px;
  }
</style>

<script>
  import { ref, defineComponent, computed } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import Constants from "@/utils/constants";
  import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
  import Helper from "@/utils/helper";
  import Auth from "@/utils/auth";
  import { useUserStore } from "@/stores/userStore.js";
  import ErrorDialog from "@/components/ErrorDialog.vue";
  import { useRxDBStore } from "@/stores/rxDBStore";
  import { useMainMenu } from "@/composables/useMainMenu";
  import { useProfileStore } from "@/stores/profileStore";
  export default defineComponent({
    components: {
      ConfirmationDialog,
      ErrorDialog
    },
    setup() {
      const router = useRouter();
      const route = useRoute();
      const logoutDialog = ref(false);
      const userStore = useUserStore();
      const rxDBStore = useRxDBStore();
      const profileStore = useProfileStore();

      const { companyOptions, selectedCompany, menuItems, filteredMenuItems } =
        useMainMenu();

      const homeRoute = computed(() => {
        const home = (menuItems.value || []).find((i) => i?.label === "Home");
        return home?.route || null;
      });

      // Load once; subsequent updates are reactive via profileStore state
      profileStore.loadHomePageFavorites();

      const visibleMenuItems = computed(() => {
        const items = filteredMenuItems.value || [];
        return items.filter(
          (item) =>
            item?.company?.includes(selectedCompany.value) ||
            (item?.company || []).length === 0 ||
            Helper.isCurrentEnvironmentDev()
        );
      });

      const starredMenuItems = computed(() => {
        const favSet = new Set(profileStore.homePageFavorites || []);
        if (homeRoute.value) favSet.add(homeRoute.value);

        return visibleMenuItems.value.filter((item) => {
          if (item?.label === "Home") return true;
          return !!item?.route && favSet.has(item.route);
        });
      });

      const nonStarredMenuItems = computed(() => {
        const favSet = new Set(profileStore.homePageFavorites || []);
        if (homeRoute.value) favSet.add(homeRoute.value);

        return visibleMenuItems.value.filter((item) => {
          if (item?.label === "Home") return false; // always in starred section
          return !favSet.has(item.route);
        });
      });

      const menubarItems = computed(() => {
        const starred = starredMenuItems.value || [];
        return starred;
      });

      async function logOutConfirm() {
        try {
          // this will preserve the info, so if user gets logout it won't purge
          await Auth.signOut();
          await userStore.setIsUserSignedIn(false);
          await Helper.resetPiniaStore();
          router.replace(Constants.enums.ROUTE_ENUM.LOG_OUT); // Redirect to login page after logout
        } catch (error) {
          console.log("logout error :", error);
        }
      }
      const profileMenuRef = ref();

      const profileMenu = ref([
        {
          label: "Logout",
          icon: "pi pi-sign-out",
          clickable: true,
          command: () => {
            logoutDialog.value = true;
          }
        },
        {
          label: userStore.isOnline ? "Online" : "Offline",
          icon: "pi pi-circle-fill",
          iconColor: userStore.isOnline ? "green" : "red"
        },
        {
          label: computed(() =>
            rxDBStore.getSyncedAt
              ? Helper.convertToLocalDate(
                  rxDBStore.getSyncedAt,
                  "DD/MM/YYYY hh:mm A"
                )
              : "Not Synced"
          ),
          icon: "pi pi-sync"
        },
        {
          label: `v${Helper.getCurrentVersion()}`,
          icon: "pi pi-hashtag"
        },
        {
          label: "Settings",
          icon: "pi pi-cog",
          clickable: true,
          command: () => {
            onMenuClick(Constants.enums.ROUTE_ENUM.SETTINGS);
          }
        }
      ]);

      const profileToggle = (event) => {
        profileMenuRef.value.toggle(event);
      };

      const onMenuClick = (routePath) => {
        Helper.handleRouteNavigation(router, route, routePath);
      };

      const isActiveRoute = (routePath) => {
        return (routePath && routePath.includes(route.path)) || false;
      };

      const onErrorDialogClose = () => {
        errorObj.value.isVisible = false;
        errorObj.value.errorMsg = "";
      };

      const errorObj = ref({
        isVisible: false,
        errorMsg: ""
      });

      return {
        Helper,
        logoutDialog,
        filteredMenuItems,
        starredMenuItems,
        nonStarredMenuItems,
        menubarItems,
        profileMenuRef,
        profileMenu,
        userStore,
        onMenuClick,
        profileToggle,
        logOutConfirm,
        isActiveRoute,
        selectedCompany,
        companyOptions,
        onErrorDialogClose,
        errorObj,
        menuItems
      };
    }
  });
</script>
